import scrollLock from 'scroll-lock';
import anime from 'animejs/lib/anime.es.js';

export default class Preloader {
	static create(chapter) {
		const preloaderElement = chapter.container.querySelector('[data-preloader]');

		if (!preloaderElement) {
			return null;
		}

		return new Preloader(preloaderElement, chapter);
	}

	constructor(el, chapter) {
		this.el = el;
		this.chapter = chapter;
		this.loadingProgress = 0;

		this.loading = false;

		this.startButtonElement = this.el.querySelector('[data-preloader-start-button]');
		this.loadingPercentage = this.el.querySelector('[data-preloader-percentage]');
		this.startWithoutSoundButtonElement = this.el.querySelector('[data-preloader-start-without-sound-button]');
		this.progressBarElement = this.el.querySelector('[data-preloader-progress-bar]');
		this.imagesElement = this.el.querySelector('[data-preloader-images]');
		this.imageElements = this.el.querySelectorAll('[data-preloader-image]');
		this.introTextElement = this.el.querySelector('[data-intro-text]');
		this.introTextChunks = this.el.querySelectorAll('[data-intro-text-chunk]');

		this.preloaderAudio = this.el.querySelector('[data-preloader-audio]');

		anime.set(this.progressBarElement, {
			scaleX: 0,
		});
	}

	get active() {
		return this.el.classList.contains('is-active');
	}

	start() {
		return new Promise(async resolve => {
			this.loading = true;
			this.show();
			this.startGallery();
			await this.preload();
			this.stopGallery();
			this.fadeOutAudioVolume(3000);
			this.el.classList.add('has-loaded');
			await this.animateProgressBarAfterPreload();
			await this.showIntroText();
			this.loading = false;

			try {
				if (!this.chapter.audio) {
					throw 'No audio!';
				}

				await this.chapter.audio.play();
			} catch (error) {
				await this.activateStartButton();
			}

			this.hide();
			resolve();

			if (this.chapter.videos) {
				this.chapter.videos.handleScroll();

				// Initialize the remaining videos
				const unreadyVideoPlayers = this.chapter.videos.getUnreadyVideoPlayers();
				this.chapter.videos.loadVideoPlayers(unreadyVideoPlayers);
			}
		});
	}

	async showIntroText() {
		await anime({
			targets: this.introTextElement,
			opacity: 1,
			easing: 'linear',
			duration: 300,
		}).finished;

		await anime({
			targets: this.introTextChunks,
			opacity: 1,
			easing: 'linear',
			duration: 600,
			delay: anime.stagger(200),
		}).finished;

		return anime({
			targets: this.introTextElement,
			opacity: 0,
			easing: 'easeOutSine',
			duration: 2000,
			delay: 1000,
		}).finished;
	}

	startGallery() {
		let count = 0;
		this.imagesElement.classList.add('is-active');

		const showImage = () => {
			count = (count + 1) % this.imageElements.length;
			this.imageElements.forEach((image, index) => {
				if (index == count) {
					image.classList.add('is-active');
				} else {
					image.classList.remove('is-active');
				}
			});
			count = (count + 1) % this.imageElements.length;
		};

		showImage();
		this.imagesTimer = setInterval(showImage, 2000);
	}

	stopGallery() {
		this.imagesElement.classList.remove('is-active');

		if (this.imagesTimer) {
			clearInterval(this.imagesTimer);
			this.imagesTimer = null;
		}
	}

	increaseProgressBar(amount) {
		this.loadingProgress += amount;
		this.loadingPercentage.innerHTML = parseInt(this.loadingProgress);

		this.progressBarAnimation = anime({
			targets: this.progressBarElement,
			scaleX: this.loadingProgress / 100,
			duration: 300,
			easing: 'linear',
		});
	}

	async animateProgressBarAfterPreload() {
		await this.progressBarAnimation.finished;

		this.progressBarElement.style.transformOrigin = 'right';

		const animation = anime({
			targets: this.progressBarElement,
			scaleX: 0,
			duration: 1500,
			easing: 'easeInOutCubic',
		});

		return animation.finished;
	}

	activateStartButton() {
		return new Promise(resolve => {
			this.startButtonElement.style.visibility = 'visible';
			this.startWithoutSoundButtonElement.style.visibility = 'visible';

			anime({
				targets: this.startButtonElement,
				opacity: 1,
				duration: 1800,
				easing: 'linear',
			});

			anime({
				targets: this.startWithoutSoundButtonElement,
				opacity: 1,
				duration: 1800,
				easing: 'linear',
			});

			this.startButtonElement.addEventListener(
				'click',
				async e => {
					try {
						if (!this.chapter.audio) {
							throw 'No audio!';
						}

						await this.chapter.audio.play();
					} catch (error) {
					}

					anime({
						targets: this.startWithoutSoundButtonElement,
						opacity: 0,
						duration: 1200,
						easing: 'linear',
					});

					await anime({
						targets: this.startButtonElement,
						opacity: 0,
						duration: 1200,
						easing: 'linear',
					}).finished;

					resolve();
				},
				{
					once: true,
				},
			);

			this.startWithoutSoundButtonElement.addEventListener(
				'click',
				async e => {
					try {
						if (!this.chapter.audio) {
							throw 'No audio!';
						}

						this.chapter.audio.mute();
						await this.chapter.audio.play();
					} catch (error) {}

					anime({
						targets: this.startWithoutSoundButtonElement,
						opacity: 0,
						duration: 900,
						easing: 'linear',
					});

					await anime({
						targets: this.startButtonElement,
						opacity: 0,
						duration: 900,
						easing: 'linear',
					}).finished;

					resolve();
				},
				{
					once: true,
				},
			);
		});
	}

	imageLoaded(total) {
		// 40

		if (!this.loading) {
			return;
		}

		const increment = 40 / total;
		this.increaseProgressBar(increment);
	}

	async loadSource(src) {
		// const res = await fetch(src);
		// const blob = await res.blob();
		// return URL.createObjectURL(blob);

		let response = await fetch(src);
		const reader = response.body.getReader();
		const contentLength = +response.headers.get('Content-Length');
		let receivedLength = 0;
		let chunks = [];

		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				break;
			}

			chunks.push(value);
			receivedLength += value.length;
		}

		return URL.createObjectURL(new Blob(chunks, { type: 'audio/mpeg' }));
	}

	async loadPreloaderAudio(preloaderAudioSource) {
		return await this.loadSource(preloaderAudioSource)
	}

	playPreloaderAudio(preloaderAudioSource) {
		return new Promise(async resolve => {

			if(preloaderAudioSource) {
				this.preloaderAudio.src = preloaderAudioSource;
				this.preloaderAudio.addEventListener('ended', () => {
					resolve();
				});
				this.preloaderAudio.addEventListener('error', () => {
					resolve();
				});
				try {
					await this.preloaderAudio.play();
				} catch (error) {
					resolve();
				}
			} else {
				resolve();
			}
		});
	}

	async preload() {
		const promises = [];

		const audioIntroPromise = new Promise((resolve, reject) => {
			const preloaderAudioSource = this.preloaderAudio.getAttribute('data-source');

			if(preloaderAudioSource) {
				this.loadPreloaderAudio(preloaderAudioSource).then((objectUrl) => {
					this.playPreloaderAudio(objectUrl).then(() => {
						resolve();
					}).catch(() => {
						resolve();
					});
				}).catch(() => {
					resolve();
				});
			} else {
				resolve();
			}
		});

		audioIntroPromise.finally(() => {
			this.increaseProgressBar(10);
		});

		promises.push(audioIntroPromise);

		// Audio
		if (this.chapter.audio) {
			const chapterPartsAudio = this.chapter.audio.loadChapterParts().then(() => {
				this.chapter.audio.src = this.chapter.parts[0].data.object_url;
				this.increaseProgressBar(10);
			});
			promises.push(chapterPartsAudio);
		} else {
			this.increaseProgressBar(10);
		}

		// Images
		if (this.chapter.images) {
			const notLoadedImages = this.chapter.images.getNotLoadedImages();
			const loadedImages = this.chapter.images.loadImages(notLoadedImages);
			promises.push(loadedImages);
		} else {
			this.increaseProgressBar(40);
		}

		// Videos
		if (this.chapter.videos) {
			// "Preload" the first two videos in each chapter part
			const unreadyVideoPlayers = this.chapter.videos.getUnreadyVideoPlayersPerPart(2);
			const readyVideoPlayers = this.chapter.videos.loadVideoPlayers(unreadyVideoPlayers).then(() => {
				this.increaseProgressBar(40);
			});
			promises.push(readyVideoPlayers);
		} else {
			this.increaseProgressBar(40);
		}

		return Promise.all(promises);
	}

	show() {
		window.scrollTo(0, 0);
		scrollLock.disablePageScroll();
	}

	async hide() {
		scrollLock.enablePageScroll();
		window.scrollTo(0, 0);

		await anime({
			targets: this.el,
			opacity: 0,
			easing: 'linear',
			duration: 3000,
		}).finished;

		this.destroy();

	}

	setVolume(volume = null) {
		if (volume) {
			this.preloaderAudio.volume = volume;
		}
		this.volume = this.preloaderAudio.volume;
	}

	fadeOutAudioVolume(duration = 3000) {
		this.setVolume();

		this.fadeOutTimer = setInterval(() => {
			if (this.preloaderAudio.volume <= 0) {
				clearInterval(this.fadeOutTimer);
				this.fadeOutTimer = null;
				this.pause();
				return;
			}

			this.preloaderAudio.volume = Math.max(0, this.preloaderAudio.volume - 0.05);
		}, duration / 20);
	}

	pause() {
		if (!this.preloaderAudio) {
			return;
		}

		this.preloaderAudio.pause();
	}

	destroy() {
		this.el.style.visibility = 'hidden';
		this.el.remove();

		clearInterval(this.fadeOutTimer);
		this.pause();
		this.preloaderAudio.remove();
	}

}
